import { Container, ResolutionContext, ServiceIdentifier } from 'inversify'

import 'reflect-metadata'

export function containerFactory() {
  return new Container()
}

/* eslint-disable  @typescript-eslint/no-explicit-any */
export type PluginMethod<T, M extends (...args: any) => any> = (
  context: ResolutionContext,
  subject: T,
  proceed: (args: Parameters<M>) => ReturnType<M>, // next(arguments)
  argumentsList: Parameters<M>
) => ReturnType<M>

/* eslint-disable  @typescript-eslint/no-explicit-any */
export function registerPlugin<T, M extends (...args: any) => any>(
  container: Container,
  serviceIdentifier: ServiceIdentifier<T>,
  method: keyof T,
  plugin: PluginMethod<T, M>
) {
  container.onActivation<T>(serviceIdentifier, (context, injectable) => {
    const handler: ProxyHandler<M> = {
      apply: (target: M, thisArg: T, argumentsList: Parameters<M>) => {
        const next = (args: Parameters<M>) => {
          return target.apply(thisArg, args)
        }
        return plugin(context, thisArg, next, argumentsList)
      },
    }

    // @ts-expect-error proxy constructor error
    injectable[method] = new Proxy(injectable[method], handler)
    return injectable
  })
}

// Generic plugin factory function. Serves as boilerplate for creating plugins.
export function createPlugin<T, M extends (...args: any) => any>(
  pluginHandler: (
    subject: T,
    proceed: (args: Parameters<M>) => ReturnType<M>,
    argumentsList: Parameters<M>,
    context?: ResolutionContext
  ) => ReturnType<M>
): PluginMethod<T, M> {
  return (context, subject, proceed, argumentsList) => {
    return pluginHandler(subject, proceed, argumentsList, context)
  }
}

/*
// registerPlugin usage example
registerPlugin<UrlService, UrlService['getUrlEntityByPath']>(
  container,
  TOKENS.Url,
  'getUrlEntityByPath',
  (context, subject, proceed, argumentsList) => {
    console.log(
      `-> getUrlEntityByPath Before: ${new Date().getTime()} | ${argumentsList}`
    )
    const result = proceed(argumentsList)
    result.then(() => {
      console.log(`-> getUrlEntityByPath After: ${new Date().getTime()}`)
    })
    return result
  }
)

// Low level plugins implementation example
import { TOKENS, UrlService } from '@maestro/data-layer'
container.onActivation<UrlService>(TOKENS.Url, (context, urlProvider) => {
  const handler: ProxyHandler<UrlService['getUrlEntityByPath']> = {
    apply: function (target, thisArgument, argumentsList: [path: string]) {
      console.log(
        `-> getUrlEntityByPath Before: ${new Date().getTime()} | ${argumentsList}`
      )
      const result = target.apply(thisArgument, argumentsList)
      console.log(`-> getUrlEntityByPath After: ${new Date().getTime()}`)
      return result
    },
  }

  // urlProvider['getUrlEntityByPath'] = new Proxy(
  urlProvider.getUrlEntityByPath = new Proxy(
    urlProvider.getUrlEntityByPath,
    handler
  )

  return urlProvider
})
*/

export type ConverterExtender<M extends (...args: any) => any> = (
  ...args: [...Parameters<M>, ReturnType<M>]
) => ReturnType<M>

export function createConverterExtender<M extends (...args: any) => any>(
  handler: (...args: [...Parameters<M>, ReturnType<M>]) => ReturnType<M>
): ConverterExtender<M> {
  return handler
}
